// shared/UpcomingMacroEvents.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './marketEvents.css'; // reuse the same styles

// ---- helpers ----

function classifyImpact(ev) {
  const impact = (ev.impact || '').toLowerCase();
  if (['high', 'medium', 'low'].includes(impact)) return impact;
  const strength = (ev.strength || '').toLowerCase();

  if (strength.includes('strong')) return 'high';
  if (strength.includes('high')) return 'high';
  if (strength.includes('medium')) return 'medium';
  if (strength.includes('weak')) return 'low';
  if (strength.includes('low')) return 'low';

  const name = (ev.name || '').toLowerCase();
  const HIGH_KEYWORDS = [
    'nonfarm payroll',
    'unemployment rate',
    'cpi',
    'consumer price',
    'core cpi',
    'pce',
    'interest rate',
    'rate decision',
    'fomc',
    'gdp',
    'gross domestic product',
    'ism',
    'pmi',
    'retail sales',
    'consumer sentiment',
  ];
  if (HIGH_KEYWORDS.some(k => name.includes(k))) return 'high';

  return 'medium';
}

function impactClass(ev) {
  const base = 'ts-econ-impact';
  const level = classifyImpact(ev);

  if (level === 'high') return `${base} ts-econ-impact-high`;
  if (level === 'low') return `${base} ts-econ-impact-low`;
  return `${base} ts-econ-impact-med`;
}

// ---- Component ----

export default function UpcomingMacroEvents({ lookaheadDays = 7, limit = 20 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isLocal = window.location.hostname === 'localhost';
  const API_BASE = isLocal
    ? 'http://localhost:5000'
    : 'https://www.trendscout.club';

  // fetch snapshot once
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API_BASE}/api/market-events/economic-events`,
          { params: { days: lookaheadDays } }
        );

        if (!cancelled) {
          const evs = (res.data?.events || []).map((ev, i) => ({
            ...ev,
            _id: ev._id || ev.id || `${i}-${ev.name}-${ev.rawDate}`,
          }));
          setEvents(evs);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load macro events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, lookaheadDays]);

  // Include scheduled entries with numeric forecasts, including 0 and negatives.
  // Speeches without forecasts remain excluded.
  const upcoming = useMemo(() => {
    if (!events.length) return [];

    const now = new Date();
    const end = new Date(now.getTime() + lookaheadDays * 24 * 60 * 60 * 1000);

    return events
      .filter(ev => {
        if (!ev.date) return false;
        const d = new Date(ev.date);
        if (Number.isNaN(d.getTime())) return false;

        const hasForecast =
          typeof ev.forecast === 'number' && Number.isFinite(ev.forecast);

        // upcoming + has forecast (no speeches)
        return d > now && d <= end && hasForecast;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, limit);
  }, [events, limit, lookaheadDays]);

  return (
    <div className="details-panel market-snapshot-panel">
      <div className="ts-econ-header ts-econ-header-compact">
        <div>
          <h3 className="ts-econ-title">Upcoming macro releases</h3>
        </div>
      </div>

      {loading && (
        <div className="ts-econ-status">Loading upcoming events…</div>
      )}
      {error && <div className="ts-econ-error">{error}</div>}

      {!loading && !error && upcoming.length === 0 && (
        <div className="ts-econ-empty">
          No upcoming macro releases with forecasts.
        </div>
      )}

      {!loading && !error && upcoming.length > 0 && (
        <div className="ts-econ-body ts-econ-body-compact">
          <ul className="ts-econ-list">
            {upcoming.map(ev => (
              <li key={ev._id} className="ts-econ-item">
                {/* time (Lithuania) */}
                <div className="ts-econ-time">
                  {ev.date
                    ? new Date(ev.date).toLocaleTimeString('lt-LT', {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Europe/Vilnius',
                      })
                    : '—'}
                </div>

                {/* name + badges */}
                <div className="ts-econ-main">
                  <div className="ts-econ-event">{ev.name}</div>
                  <div className="ts-econ-meta">
                    {ev.currency && (
                      <span className="ts-econ-currency">
                        {ev.currency}
                      </span>
                    )}
                    <span className={impactClass(ev)}>
                      {classifyImpact(ev) === 'high'
                        ? 'High impact'
                        : classifyImpact(ev) === 'medium'
                        ? 'Medium impact'
                        : 'Low impact'}
                    </span>
                  </div>
                </div>

                {/* forecast/prev only */}
                <div className="ts-econ-values">
                  {ev.forecast != null && (
                    <span className="ts-econ-val">
                      F: <b>{ev.forecast}</b>
                    </span>
                  )}
                  {ev.previous != null && (
                    <span className="ts-econ-val">
                      Prev: {ev.previous}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
